import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  Settings as SettingsIcon,
  Refresh,
  CloudDownload,
  BarChart,
} from "@mui/icons-material";
import { Layout } from "../Layouts/Layout";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_COURSE,
  CourseResult,
  CHECK_BLUEPRINT_IMPORT,
  OBTAIN_LMS_TOKEN,
  SET_HIDE_OVERALL_AVERAGES,
} from "../Queries";
import { useErrorBoundary } from "../Contexts/ErrorContext";

// Helper function to determine alert severity based on message content
const getAlertSeverity = (
  message: string,
): "error" | "warning" | "info" | "success" => {
  if (!message) return "info";

  const lowerMessage = message.toLowerCase().trim();

  // Check most specific patterns first, then fall back to general patterns

  // Success conditions - successful operations (most specific first)
  if (
    lowerMessage.includes("successfully imported") ||
    lowerMessage.includes("refreshed successfully") ||
    (lowerMessage.includes("successfully") &&
      lowerMessage.includes("imported") &&
      !lowerMessage.includes("already"))
  ) {
    return "success";
  }

  // Info conditions - informational messages (check before warnings/errors)
  // These are status messages, not errors
  if (
    lowerMessage.includes("already been imported") ||
    lowerMessage.includes("already imported") ||
    lowerMessage.includes("associated with blueprint") ||
    lowerMessage.includes("not associated with a blueprint") ||
    lowerMessage.includes("not associated with a blueprint course")
  ) {
    return "info";
  }

  // Warning conditions - cautionary states that need attention (check before errors)
  // "not found" is typically a warning, not an error
  if (lowerMessage.includes("warning") || lowerMessage.includes("not found")) {
    return "warning";
  }

  // Error conditions - actual failures (check after more specific patterns)
  // Only match if it's clearly an error, not just containing the word
  if (
    lowerMessage.startsWith("error:") ||
    (lowerMessage.includes("error:") && !lowerMessage.includes("no error")) ||
    lowerMessage.includes("failed") ||
    lowerMessage.includes("unable") ||
    lowerMessage.includes("missing authorization") ||
    lowerMessage.includes("no permission") ||
    lowerMessage.includes("permission to check")
  ) {
    return "error";
  }

  // Success fallback - if it says "successfully" anywhere (but not already matched)
  if (lowerMessage.includes("successfully")) {
    return "success";
  }

  // Default to info for informational messages
  return "info";
};

export function Settings() {
  const { setError } = useErrorBoundary();
  const [refreshMessage, setRefreshMessage] = useState<string | null>(null);
  const [importMessage, setImportMessage] = useState<string | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);

  const { data: courseData } = useQuery<CourseResult>(GET_COURSE, {
    onError: (error) => setError("Error loading course", error),
  });

  const [obtainLmsToken] = useMutation(OBTAIN_LMS_TOKEN);

  const [setHideOverallAverages] = useMutation(SET_HIDE_OVERALL_AVERAGES, {
    onError: (error) => setError("Error updating setting", error),
  });

  const course = courseData?.course;
  const oauthUrl = course?.oauthUrl;
  const hideOverallAverages = course?.hideOverallAverages ?? false;

  const handleHideOverallAveragesChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    await setHideOverallAverages({
      variables: { hide: event.target.checked },
    });
  };

  const handleRefreshConnection = () => {
    if (!oauthUrl) {
      setRefreshMessage("OAuth connection is not available for this course.");
      return;
    }

    // Open OAuth URL in a popup window
    const width = 600;
    const height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    const popup = window.open(
      oauthUrl,
      "oauth",
      `width=${width},height=${height},left=${left},top=${top}`,
    );

    if (!popup) {
      setRefreshMessage("Please allow popups to refresh your connection.");
      return;
    }

    // Listen for OAuth success message
    const handleMessage = async (event: MessageEvent) => {
      if (event.data.type === "OAUTH_SUCCESS") {
        // For Moodle, no token exchange needed
        if (event.data.platform === "moodle") {
          setRefreshMessage("Connection refreshed successfully!");
          popup.close();
          window.removeEventListener("message", handleMessage);
          setTimeout(() => {
            window.location.reload();
          }, 1000);
          return;
        }

        // For other platforms, exchange the code for a token
        if (event.data.code && event.data.state) {
          setRefreshLoading(true);
          setRefreshMessage(null);

          try {
            await obtainLmsToken({
              variables: {
                code: event.data.code,
                state: event.data.state,
              },
            });

            setRefreshMessage("Connection refreshed successfully!");
            popup.close();
            window.removeEventListener("message", handleMessage);
            // Reload the page to refresh the course data
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          } catch (error: any) {
            console.error("OAuth token exchange failed:", error);
            setRefreshMessage(
              `Error: ${error.message || "Failed to refresh connection"}`,
            );
            popup.close();
            window.removeEventListener("message", handleMessage);
          } finally {
            setRefreshLoading(false);
          }
        } else {
          setRefreshMessage("Error: Missing authorization code or state");
          popup.close();
          window.removeEventListener("message", handleMessage);
        }
      } else if (event.data.type === "OAUTH_ERROR") {
        setRefreshMessage(
          `Error: ${event.data.error || "Failed to refresh connection"}`,
        );
        popup.close();
        window.removeEventListener("message", handleMessage);
        setRefreshLoading(false);
      }
    };

    window.addEventListener("message", handleMessage);

    // Cleanup listener if popup is closed manually
    const checkClosed = setInterval(() => {
      if (popup.closed) {
        window.removeEventListener("message", handleMessage);
        clearInterval(checkClosed);
        setRefreshLoading(false);
      }
    }, 500);
  };

  const [checkBlueprintImport] = useMutation(CHECK_BLUEPRINT_IMPORT, {
    onCompleted: (data) => {
      setImportMessage(data.checkBlueprintImport);
      setImportLoading(false);
    },
    onError: (error) => {
      const errorMessage = error.message.toLowerCase();

      // Check if error is related to authorization/scopes
      if (
        errorMessage.includes("unauthorized") ||
        errorMessage.includes("401") ||
        errorMessage.includes("scope") ||
        errorMessage.includes("permission")
      ) {
        setImportMessage(
          `Error: ${error.message}. Please refresh your LMS API connection above to enable blueprint features.`,
        );
      } else {
        setImportMessage(`Error: ${error.message}`);
      }

      setImportLoading(false);
      setError("Error checking blueprint import", error);
    },
  });

  const handleCheckBlueprintImport = async () => {
    setImportLoading(true);
    setImportMessage(null);
    try {
      await checkBlueprintImport();
    } catch (error) {
      // Error handled in onError callback
    }
  };

  return (
    <Layout breadcrumbs={[{ title: "Settings" }]}>
      <Box sx={{ maxWidth: 800, margin: "0 auto", padding: 2 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <SettingsIcon />
          Course Settings
        </Typography>

        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <Refresh />
              Refresh LMS API Connection
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Reconnect your LMS API connection to ensure you have the latest
              permissions and scopes. This is useful if new features (like
              blueprint course support) have been enabled.
            </Typography>
            {refreshMessage && (
              <Alert severity={getAlertSeverity(refreshMessage)} sx={{ mb: 2 }}>
                {refreshMessage}
              </Alert>
            )}
            <Button
              variant="contained"
              startIcon={
                refreshLoading ? <CircularProgress size={20} /> : <Refresh />
              }
              onClick={handleRefreshConnection}
              disabled={!oauthUrl || refreshLoading}
            >
              {refreshLoading ? "Refreshing..." : "Refresh Connection"}
            </Button>
            {!oauthUrl && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                OAuth connection is not available for this LMS platform.
              </Typography>
            )}
          </CardContent>
        </Card>

        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <CloudDownload />
              Blueprint Course Import
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Manually check if this course is associated with a blueprint
              course and import surveys if they haven&apos;t been imported yet.
              This is useful if the automatic import didn&apos;t run during the
              initial course launch.
            </Typography>
            {importMessage && (
              <Alert severity={getAlertSeverity(importMessage)} sx={{ mb: 2 }}>
                {importMessage}
              </Alert>
            )}
            <Button
              variant="contained"
              startIcon={
                importLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <CloudDownload />
                )
              }
              onClick={handleCheckBlueprintImport}
              disabled={importLoading}
            >
              {importLoading ? "Checking..." : "Check for Blueprint Import"}
            </Button>
          </CardContent>
        </Card>

        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <BarChart />
              Hide Overall Average Ratings
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              When enabled, suppress the top-level overall average ratings from
              student released result views and PDF exports. Section and
              question-level averages are unaffected.
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={hideOverallAverages}
                  onChange={handleHideOverallAveragesChange}
                  inputProps={{
                    "aria-label": "Hide Overall Average Ratings",
                  }}
                />
              }
              label={
                hideOverallAverages
                  ? "Overall averages hidden from students"
                  : "Overall averages visible to students"
              }
            />
          </CardContent>
        </Card>
      </Box>
    </Layout>
  );
}
